/**
 * Flow model.
 *
 * This is the de-facto spec for Flow objects used here, in the editor, and across macOS/iOS.
 * @packageDocumentation
 * @module model
 * @preferred
 */
import Screens from "./Screens";
import guidGenerator, {
  getCurrentDate,
} from "../features/utils/commonFunctions";

/**
 * Many fields are stored as "NO" or "YES" instead of booleans
 */
export type CapitalBool = "NO" | "YES";

/**
 * Flow Transitions are stored as `number`s.
 *
 * But, they can be one of Slide, Fade, Instant.
 * These modes can be set as the transition type by setting the type to be the index in this array
 */
export const FlowTransitions = ["Slide", "Fade", "Instant"];

export default class Flow {
  autoAdvanceLoops: CapitalBool;
  created: string;
  id: string;
  screens: Screens[];
  themeId: string;
  title: string;
  updated: string;
  /**
   * Explicit height and width to display Flow (in pixels)
   */
  viewSize: {
    height: number;
    width: number;
  };
  autoAdvanceSlideDuration?: number;
  autoAdvanceSlideTransitionType?: number;

  constructor(
    autoAdvanceLoops: CapitalBool,
    created = getCurrentDate(),
    id = "",
    screens: Screens[],
    themeId: string,
    title: string,
    updated = getCurrentDate(),
    viewSize: { height: number; width: number },
    autoAdvanceSlideDuration?: number,
    autoAdvanceSlideTransitionType?: number
  ) {
    this.autoAdvanceLoops = autoAdvanceLoops;
    this.created = created;
    this.id = id;
    this.screens = screens;
    this.themeId = themeId;
    this.title = title;
    this.updated = updated;
    this.viewSize = viewSize;
    this.autoAdvanceSlideDuration = autoAdvanceSlideDuration;
    this.autoAdvanceSlideTransitionType = autoAdvanceSlideTransitionType;
  }

  setPreviewScreenshots(idx: number, val: string, treemoId: string) {
    if (!this.screens[idx] || !this.screens[idx].previewScreenShot) return;
    this.screens[idx].previewScreenShot.externalURL = {
      __tag: "string",
      value: val,
    };
    this.screens[idx].previewScreenShot.treemoContentID = treemoId;
    this.screens[idx].previewScreenShot.updated = getCurrentDate();
    if (this.screens[idx].previewScreenShot_textless) {
      this.screens[idx].previewScreenShot_textless!.externalURL = {
        __tag: "string",
        value: val,
      };
      this.screens[idx].previewScreenShot_textless!.treemoContentID = treemoId;
      this.screens[idx].previewScreenShot_textless!.updated = getCurrentDate();
    }
    this.updated = getCurrentDate();
    this.screens[idx].updated = getCurrentDate();
  }

  toggleAutoAdvance(): void {
    this.autoAdvanceLoops = this.autoAdvanceLoops == "YES" ? "NO" : "YES";
  }

  setTransitionType(tt: number): void {
    this.autoAdvanceSlideTransitionType = tt;
  }

  setAutoAdvanceDuration(duration: number): void {
    this.autoAdvanceSlideDuration = duration;
    console.log(this.autoAdvanceSlideDuration);
  }

  static fromJSON(json: any): Flow {
    let flow = Object.create(Flow.prototype);
    return Object.assign(flow, json, {
      screens: (json.screens || []).map((screen: any) => Screens.fromJSON(screen)),
    });
  }

  static reviver(key: string, value: any): any {
    return key === "" ? Flow.fromJSON(value) : value;
  }

  static default(): Flow {
    return new Flow(
      "NO",
      getCurrentDate(),
      guidGenerator(),
      [Screens.default()],
      "10",
      "",
      getCurrentDate(),
      { height: 748, width: 1024 }
    );
  }
}

Flow.displayName = "Flow";
